import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaSearch, 
  FaUser, 
  FaPhone, 
  FaStore, 
  FaMoneyBillWave,
  FaCalculator,
  FaCreditCard,
  FaReceipt,
  FaCheckCircle,
  FaSpinner
} from 'react-icons/fa';

const RevenueCollectionView = ({ userInfo, API_BASE_URL, auth }) => {
  const [step, setStep] = useState(1); // 1: Search, 2: Select Items, 3: Payment, 4: Complete
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [revenueItems, setRevenueItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [payerPhone, setPayerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('momo');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success' or 'error'
    title: '',
    message: ''
  });

  useEffect(() => {
    fetchRevenueItems();
  }, []);

  const fetchRevenueItems = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/revenue-items/mmda/${userInfo.mmda_id}`,
        auth
      );
      setRevenueItems(response.data);
    } catch (error) {
      console.error('Error fetching revenue items:', error);
    }
  };

  const searchTraders = async () => {
    if (!searchTerm.trim()) return;

    // Validate zone assignment
    if (!userInfo.zone_id) {
      console.warn('No zone assigned to field officer');
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      // Determine if it's a phone search or name search
      const isPhoneSearch = /^[0-9+\-\s()]+$/.test(searchTerm) || searchTerm.length >= 10;
      const searchParam = isPhoneSearch ? 'phone' : 'name';

      const response = await axios.get(
        `${API_BASE_URL}/traders/search?${searchParam}=${searchTerm}&userRole=Revenue Officer&mmda_id=${userInfo.mmda_id}&zone_id=${userInfo.zone_id}`,
        auth
      );

      // Filter results by zone if backend doesn't filter properly
      let filteredResults = response.data;
      if (userInfo.zone_id) {
        filteredResults = response.data.filter(trader =>
          trader.zone_id === userInfo.zone_id || trader.zone_id === parseInt(userInfo.zone_id)
        );
      }

      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching traders:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const selectTrader = async (trader) => {
    setSelectedTrader(trader);
    setPayerPhone(trader.phone);
    
    // Initiate collection session
    try {
      const response = await axios.post(
        `${API_BASE_URL}/revenue-collection/initiate`,
        {
          trader_id: trader.trader_id,
          revenue_officer_id: userInfo.user_id,
          mmda_id: userInfo.mmda_id,
          zone_id: userInfo.zone_id
        },
        auth
      );
      setSessionId(response.data.session_id);
      setStep(2);
    } catch (error) {
      console.error('Error initiating collection:', error);
    }
  };

  const toggleRevenueItem = (item) => {
    const isSelected = selectedItems.find(si => si.item_id === item.item_id);
    if (isSelected) {
      setSelectedItems(selectedItems.filter(si => si.item_id !== item.item_id));
    } else {
      setSelectedItems([...selectedItems, { ...item, quantity: 1 }]);
    }
  };

  const updateQuantity = (itemId, quantity) => {
    setSelectedItems(selectedItems.map(item => 
      item.item_id === itemId ? { ...item, quantity: Math.max(1, quantity) } : item
    ));
  };

  const calculateTotal = async () => {
    if (selectedItems.length === 0) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/revenue-collection/calculate`,
        {
          session_id: sessionId,
          revenue_items: selectedItems.map(item => ({
            item_id: item.item_id,
            quantity: item.quantity
          }))
        },
        auth
      );
      setTotalAmount(response.data.total_amount);
      setStep(3);
    } catch (error) {
      console.error('Error calculating payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      show: false,
      type: '',
      title: '',
      message: ''
    });
  };

  const processPayment = async () => {
    if (!payerPhone.trim()) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Missing Information',
        message: 'Please enter payer phone number'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/revenue-collection/process-payment`,
        {
          session_id: sessionId,
          payer_phone: payerPhone,
          payment_method_id: 1, // Default payment method
          gateway_type: paymentMethod
        },
        auth
      );

      // Simulate payment processing delay
      setTimeout(async () => {
        try {
          const verifyResponse = await axios.get(
            `${API_BASE_URL}/revenue-collection/verify/${response.data.reference_code}`,
            auth
          );
          
          if (verifyResponse.data.verification.verification_result.status === 'successful') {
            setReceipt({
              reference: response.data.reference_code,
              amount: totalAmount,
              trader: selectedTrader,
              items: selectedItems,
              timestamp: new Date()
            });
            setStep(4);

            // Show success notification
            setNotification({
              show: true,
              type: 'success',
              title: 'Payment Successful',
              message: `Payment of GHS ${totalAmount.toFixed(2)} has been processed successfully.`
            });
          } else {
            setNotification({
              show: true,
              type: 'error',
              title: 'Payment Failed',
              message: 'Payment failed. Please try again.'
            });
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          setNotification({
            show: true,
            type: 'error',
            title: 'Verification Failed',
            message: 'Payment verification failed. Please check with admin.'
          });
        } finally {
          setLoading(false);
        }
      }, 3000);

    } catch (error) {
      console.error('Error processing payment:', error);
      setNotification({
        show: true,
        type: 'error',
        title: 'Payment Processing Failed',
        message: error.response?.data?.error || 'Payment processing failed. Please try again.'
      });
      setLoading(false);
    }
  };

  const resetCollection = () => {
    setStep(1);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedTrader(null);
    setSelectedItems([]);
    setTotalAmount(0);
    setPayerPhone('');
    setSessionId(null);
    setReceipt(null);
  };

  const renderStep1 = () => (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '2rem', color: '#2c3e50' }}>
        <FaSearch style={{ marginRight: '0.5rem' }} />
        Search Trader
      </h2>
      
      <div style={{ 
        backgroundColor: 'white', 
        padding: '2rem', 
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Search by Phone Number or Name:
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter phone number or trader name..."
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
              onKeyPress={(e) => e.key === 'Enter' && searchTraders()}
            />
            <button
              onClick={searchTraders}
              disabled={loading || !searchTerm.trim()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? <FaSpinner className="spin" /> : <FaSearch />}
              Search
            </button>
          </div>
        </div>

        {searchResults.length > 0 && (
          <div>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Search Results:</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {searchResults.map((trader) => (
                <div
                  key={trader.trader_id}
                  onClick={() => selectTrader(trader)}
                  style={{
                    padding: '1rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                >
                  <FaUser size={24} color="#3498db" />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, color: '#2c3e50' }}>
                      {trader.full_name}
                    </h4>
                    <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}>
                      <FaPhone style={{ marginRight: '0.5rem' }} />
                      {trader.phone}
                    </p>
                    {trader.business_name && (
                      <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}>
                        <FaStore style={{ marginRight: '0.5rem' }} />
                        {trader.business_name}
                      </p>
                    )}
                    <p style={{ margin: '0.25rem 0', color: '#95a5a6', fontSize: '0.8rem' }}>
                      Code: {trader.trader_id} | {trader.zone_name} | {trader.group_name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '2rem', color: '#2c3e50' }}>
        <FaCalculator style={{ marginRight: '0.5rem' }} />
        Select Revenue Items
      </h2>

      {selectedTrader && (
        <div style={{
          backgroundColor: '#e8f4fd',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '2rem',
          border: '1px solid #3498db'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
            Collecting from: {selectedTrader.full_name}
          </h4>
          <p style={{ margin: 0, color: '#7f8c8d' }}>
            Phone: {selectedTrader.phone} | Business: {selectedTrader.business_name}
          </p>
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#2c3e50' }}>Available Revenue Items:</h3>

        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {revenueItems.map((item) => {
            const isSelected = selectedItems.find(si => si.item_id === item.item_id);
            return (
              <div
                key={item.item_id}
                style={{
                  padding: '1rem',
                  border: `2px solid ${isSelected ? '#3498db' : '#ddd'}`,
                  borderRadius: '0.5rem',
                  backgroundColor: isSelected ? '#f8f9fa' : 'white',
                  cursor: 'pointer'
                }}
                onClick={() => toggleRevenueItem(item)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
                      {item.item_name}
                    </h4>
                    <p style={{ margin: 0, color: '#7f8c8d', fontSize: '0.9rem' }}>
                      {item.description}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#27ae60' }}>
                      GHS {parseFloat(item.amount || 0).toFixed(2)}
                    </div>
                    {isSelected && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Quantity:</label>
                        <input
                          type="number"
                          min="1"
                          value={isSelected.quantity}
                          onChange={(e) => updateQuantity(item.item_id, parseInt(e.target.value))}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '60px',
                            marginLeft: '0.5rem',
                            padding: '0.25rem',
                            border: '1px solid #ddd',
                            borderRadius: '0.25rem'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {selectedItems.length > 0 && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>Selected Items:</h4>
            {selectedItems.map((item) => (
              <div key={item.item_id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem'
              }}>
                <span>{item.item_name} x {item.quantity}</span>
                <span>GHS {(parseFloat(item.amount) * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
          <button
            onClick={() => setStep(1)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}
          >
            Back
          </button>
          <button
            onClick={calculateTotal}
            disabled={selectedItems.length === 0 || loading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? <FaSpinner className="spin" /> : <FaCalculator />}
            Calculate & Continue
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '1rem' }}>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {/* Add steps 3 and 4 here */}

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            {/* Icon */}
            <div style={{
              marginBottom: '1rem',
              fontSize: '3rem'
            }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 1rem 0',
              color: notification.type === 'success' ? '#27ae60' : '#e74c3c',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>
              {notification.title}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 2rem 0',
              color: '#666',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {notification.message}
            </p>

            {/* Close Button */}
            <button
              onClick={handleNotificationClose}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: notification.type === 'success' ? '#27ae60' : '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                minWidth: '100px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueCollectionView;
